import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  TARGET_RATING,
  fmtDecimal,
  fmtInt,
  monthlyTrend,
  pct,
  type MonthPoint,
  type StarStats,
} from "../sandbox/resumenData";

interface Props {
  title: string;
  months: MonthPoint[];
  stars: StarStats;
  starsCaption: string;
  whatIf?: { moved: number; avg: number } | null;
}

const signed = (n: number) => `${n >= 0 ? "+" : "−"}${fmtDecimal(Math.abs(n))}`;

/** Nota de Google pensada para el teléfono: cifra grande, gráfico que se recorre con el dedo y reparto de estrellas como lista. */
export function RatingCardMobile({ title, months, stars, starsCaption, whatIf }: Props) {
  const hasMonthly = months.some((m) => m.avg !== null);
  return (
    <div className="sbx-card rm-card">
      {hasMonthly && <MonthlyScrubber title={title} pts={months} />}
      {stars.total > 0 && <StarHistogram stars={stars} caption={starsCaption} whatIf={whatIf} />}
    </div>
  );
}

function MonthlyScrubber({ title, pts }: { title: string; pts: MonthPoint[] }) {
  const n = pts.length;
  const trend = useMemo(() => monthlyTrend(pts), [pts]);
  const defaultSel = useMemo(() => {
    for (let i = n - 1; i >= 0; i--) if (pts[i].avg !== null && !pts[i].current) return i;
    return n - 1;
  }, [pts, n]);
  const [picked, setPicked] = useState<number | null>(null);
  const sel = picked !== null && picked < n ? picked : defaultSel;

  const boxRef = useRef<HTMLDivElement>(null);
  const [W, setW] = useState(320);
  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setW(Math.max(240, el.clientWidth)));
    setW(Math.max(240, el.clientWidth));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const rated = pts.filter((p) => p.avg !== null);
  const minV = rated.length ? Math.min(...rated.map((p) => p.avg as number)) : 3;
  const lo = Math.max(1, Math.min(Math.floor(TARGET_RATING - 0.5), Math.floor(minV - 0.05)));
  const tickStep = 5 - lo > 2 ? 1 : 0.5;
  const ticks: number[] = [];
  for (let v = 5; v >= lo - 1e-9; v -= tickStep) ticks.push(v);

  const padL = 26;
  const padR = 6;
  const plotTop = 10;
  const plotH = 132;
  const plotBot = plotTop + plotH;
  const barMax = 26;
  const baseY = plotBot + 12 + barMax;
  const H = baseY + 22;
  const step = (W - padL - padR) / Math.max(1, n);
  const xOf = (i: number) => padL + step * (i + 0.5);
  const yOf = (v: number) => plotTop + ((5 - v) / (5 - lo)) * plotH;
  const maxCount = Math.max(1, ...pts.map((p) => p.count));
  const stride = n <= 12 ? 2 : Math.ceil(n / 6);

  const segments: string[] = [];
  let run: string[] = [];
  pts.forEach((p, i) => {
    if (p.avg !== null && !p.current) run.push(`${xOf(i)},${yOf(p.avg)}`);
    else {
      if (run.length > 1) segments.push(run.join(" "));
      run = [];
    }
  });
  if (run.length > 1) segments.push(run.join(" "));
  const last = n - 1;
  const cur = pts[last];
  const prevOfCur = pts[last - 1];

  const svgRef = useRef<SVGSVGElement>(null);
  const dragging = useRef(false);
  const idxAt = (clientX: number) => {
    const r = svgRef.current?.getBoundingClientRect();
    if (!r || r.width === 0) return sel;
    const x = ((clientX - r.left) / r.width) * W;
    return Math.min(n - 1, Math.max(0, Math.floor((x - padL) / step)));
  };

  const p = pts[sel];
  let prev: MonthPoint | undefined;
  for (let i = sel - 1; i >= 0; i--) {
    if (pts[i].avg !== null) {
      prev = pts[i];
      break;
    }
  }
  const delta = p.avg !== null && prev && prev.avg !== null ? p.avg - prev.avg : null;
  const onGoal = p.avg !== null && p.avg >= TARGET_RATING;
  const per = trend ? `${trend.slope >= 0 ? "+" : "−"}${fmtDecimal(Math.abs(trend.slope), 2)}` : null;

  return (
    <section className="rm-block" aria-label={title}>
      <h3 className="rm-title">{title}</h3>

      <div className="rm-readout" aria-live="polite">
        <div className="rm-score">
          <strong>{p.avg !== null ? fmtDecimal(p.avg) : "—"}</strong>
          <span>de 5</span>
        </div>
        <div className="rm-when">
          <b>
            {p.monthName} {p.year}
          </b>
          <span>
            {p.avg === null
              ? "Sin reseñas este mes"
              : `${fmtInt(p.count)} ${p.count === 1 ? "reseña" : "reseñas"}${p.current ? " · en curso" : ""}`}
          </span>
        </div>
        {p.avg !== null && (
          <div className="rm-status">
            <span className={`rm-chip ${onGoal ? "good" : "bad"}`}>{onGoal ? "En la meta" : `Bajo la meta ${fmtDecimal(TARGET_RATING)}`}</span>
            {delta !== null && prev && (
              <span className={`rm-delta ${Math.abs(delta) < 0.05 ? "flat" : delta > 0 ? "up" : "down"}`}>
                {Math.abs(delta) < 0.05 ? `Igual que ${prev.monthName}` : `${delta > 0 ? "▲" : "▼"} ${signed(delta)} vs ${prev.monthName}`}
              </span>
            )}
          </div>
        )}
      </div>

      <div className="rm-chart" ref={boxRef}>
        <svg
          ref={svgRef}
          viewBox={`0 0 ${W} ${H}`}
          width={W}
          height={H}
          role="img"
          aria-label={`${title}: ${fmtDecimal(p.avg ?? 0)} en ${p.monthName} ${p.year}`}
          onPointerDown={(e) => {
            dragging.current = true;
            setPicked(idxAt(e.clientX));
            try {
              e.currentTarget.setPointerCapture(e.pointerId);
            } catch {
              // sin captura el arrastre sigue dentro del gráfico
            }
          }}
          onPointerMove={(e) => {
            if (dragging.current) setPicked(idxAt(e.clientX));
          }}
          onPointerUp={() => {
            dragging.current = false;
          }}
          onPointerCancel={() => {
            dragging.current = false;
          }}
        >
          <rect className="rm-goal" x={padL} y={yOf(5)} width={W - padL - padR} height={yOf(TARGET_RATING) - yOf(5)} />
          {ticks.map((v) => (
            <g key={v}>
              <text className={v === TARGET_RATING ? "rm-tick goal" : "rm-tick"} x={0} y={yOf(v) + 3}>
                {fmtDecimal(v)}
              </text>
              <line className="rm-grid" x1={padL} x2={W - padR} y1={yOf(v)} y2={yOf(v)} />
            </g>
          ))}
          {!ticks.includes(TARGET_RATING) && (
            <text className="rm-tick goal" x={0} y={yOf(TARGET_RATING) + 3}>
              {fmtDecimal(TARGET_RATING)}
            </text>
          )}
          <line className="rm-goal-line" x1={padL} x2={W - padR} y1={yOf(TARGET_RATING)} y2={yOf(TARGET_RATING)} />

          <line className="rm-cross" x1={xOf(sel)} x2={xOf(sel)} y1={plotTop} y2={baseY} />

          {pts.map((q, i) => {
            const h = q.count > 0 ? Math.max(3, (q.count / maxCount) * barMax) : 0;
            const bw = Math.min(step * 0.5, 14);
            return (
              <rect
                key={q.key}
                className={`rm-bar${i === sel ? " on" : ""}`}
                x={xOf(i) - bw / 2}
                y={baseY - h}
                width={bw}
                height={h}
                rx={2}
              />
            );
          })}

          {trend && (
            <line
              className="rm-trend"
              x1={xOf(trend.from)}
              y1={yOf(Math.min(5, Math.max(lo, trend.at(trend.from))))}
              x2={xOf(last)}
              y2={yOf(Math.min(5, Math.max(lo, trend.at(last))))}
            />
          )}
          {segments.map((s) => (
            <polyline key={s} className="rm-line" points={s} />
          ))}
          {cur && prevOfCur && cur.current && cur.avg !== null && prevOfCur.avg !== null && (
            <line className="rm-line dash" x1={xOf(last - 1)} y1={yOf(prevOfCur.avg)} x2={xOf(last)} y2={yOf(cur.avg)} />
          )}
          {pts.map((q, i) =>
            q.avg === null ? null : (
              <circle
                key={q.key}
                className={`rm-dot${q.current ? " open" : ""}${i === sel ? " on" : ""}`}
                cx={xOf(i)}
                cy={yOf(q.avg)}
                r={i === sel ? 6.5 : 3}
              />
            ),
          )}
          {pts.map((q, i) =>
            (n - 1 - i) % stride === 0 ? (
              <text key={`m-${q.key}`} className={i === sel ? "rm-month on" : "rm-month"} x={xOf(i)} y={baseY + 16} textAnchor="middle">
                {n > 12 && q.key.endsWith("-01") ? `${q.label} ’${String(q.year).slice(2)}` : q.label}
                {q.current ? "*" : ""}
              </text>
            ) : null,
          )}
        </svg>
      </div>

      <div className="rm-keys">
        <span>
          <i className="goal" />
          Meta {fmtDecimal(TARGET_RATING)}+
        </span>
        {per && (
          <span>
            <i className="trend" />
            Tendencia {per} por mes
          </span>
        )}
        <span className="hint">Toca o arrastra</span>
      </div>
    </section>
  );
}

function StarHistogram({
  stars,
  caption,
  whatIf,
}: {
  stars: StarStats;
  caption: string;
  whatIf?: { moved: number; avg: number } | null;
}) {
  const avg = stars.avg ?? stars.sum / Math.max(1, stars.total);
  return (
    <section className="rm-block rh" aria-label="Reparto de estrellas">
      <h3 className="rm-title">Cómo se reparten las estrellas</h3>
      <div className="rh-body">
        <div className="rh-score">
          <strong>{fmtDecimal(avg)}</strong>
          <span>de 5</span>
          <em>{caption}</em>
        </div>
        <div className="rh-rows">
          {[5, 4, 3, 2, 1].map((s) => {
            const c = stars.counts[s] ?? 0;
            return (
              <div key={s} className={`rh-row s${s}`}>
                <span className="rh-star">{s} ★</span>
                <div className="rh-track">
                  <i style={{ width: `${(c / Math.max(1, stars.total)) * 100}%` }} />
                </div>
                <span className="rh-n">
                  <b>{fmtInt(c)}</b>
                  <em>{pct(c, stars.total)}%</em>
                </span>
              </div>
            );
          })}
        </div>
      </div>
      {whatIf && (
        <p className="rh-note">
          Si {whatIf.moved} {whatIf.moved === 1 ? "reseña de 1 estrella hubiera sido de 4" : "reseñas de 1 estrella hubieran sido de 4"}, la nota sería{" "}
          <strong>{fmtDecimal(whatIf.avg)}</strong>.
        </p>
      )}
    </section>
  );
}
